
import AppProductSkuAutocomplete from "./SaleEditProductSkuAutocomplete.vue";
import AppCalculator from "@/components/Calculator.vue";
import AppVatSelect from "@/components/SelectVat.vue";
import AppDuration from "@/components/common/DurationInput.vue";
import {Product, Sale, SaleRow, SaleType} from "@/models/base";
import {VatService} from "@/services/vat-service";
import {DecimalConfig} from "@/models/dossier-configs/decimal-config";
import Vue from "vue";
import {DossierConfigService} from "@/services/dossier-config-service";
import {UserConfigService} from "@/services/user-config-service";
import AppUnitAutocomplete from "@/components/products-table/UnitAutocomplete.vue";
import AppInputWithTextareaModal from "@/components/common/InputWithTextareaModal.vue";
import {TranslateResult} from "vue-i18n";
import {ISelectItem} from "winfakt-vue-components";
import {printLongLocalDateTime} from "../../utils/pretty-print";
import {SaleEditState} from "@/utils/sale-edit/sale-edit-state";
import {SaleEditRowState} from "@/utils/sale-edit/sale-edit-row-state";
import {AuthService, HumanFilter, ProductService, SaleService} from "@/services";
import {MODULE} from "@/config/modules";
import {isChildOf} from "winfakt-vue-components/src/utils/dom";
import {RecoveryService} from "@/services/recovery-service";
import {SaleConfigs} from "@/models/dossier-configs/sale-configs";
import AppSaleEditRowDiscount from "./SaleEditRowDiscount.vue";
import {ContactsConfig} from "@/models/dossier-configs/contacts";
import {getEmptyIQueryParams} from "@/utils/query-params";
import {BrowseID} from "@/models/browse";
import AppSaleEditRowTrackingNumberSelect from "./SaleEditRowTrackingNumberSelect.vue";
import * as services from "@/services";
import { ScannerListener } from "@/utils/scanner";
import { IOpenProductSearchBrowseData } from "@/utils/products-table/product-edit-row-state";

export default Vue.extend({
	data() {
		return {
			SaleSettings: UserConfigService.getSaleSettings(),
			compact: UserConfigService.getSaleSettings().CompactSaleOverview,
			wait: UserConfigService.getSaleSettings().WaitForEnterOnProductSearch,
			DossierConfigService,
			UserConfigService,
			fetchingProduct: false,
			toggleCalculator: false,
			calculatorRef: "",
			calculatorDecimals: 2,
			brutoOnFocus: 0
		};
	},
	props: {
		saleEditState: {
			type: Object as () => SaleEditState,
			required: true
		},
	},
	computed: {
		trackingNumberValid():boolean {
			if (!this.saleEditState.config) return true;
			if (!this.saleEditState.config.UseProductTrackingNumbers) return true;
			return true;
		},
		lng(): string {
			return this.sale.getLng();
		},
		sale(): Sale {
			return this.saleEditState.sale;
		},
		vatRegime(): number {
			return this.sale.VatRegime;
		},
		contactDiscount(): number {
			if (this.sale.Contact) {
				return this.sale.Contact.Discount;
			}
			return 0;
		},
		priceCategory(): Number {
			return this.sale.PriceCategory;
		},
		row(): SaleRow {
			return this.saleEditState.selectedRow;
		},
		state(): SaleEditRowState {
			return this.saleEditState.productEditRowState;
		},
		qtyActions(): ISelectItem<string>[] {
			return [
				{text: this.$ct("show-qty").toString(), value: "hideTimePicker"},
				{text: this.$ct("show-time").toString(), value: "showTimePicker"}
			];
		},
		contactsConfig(): ContactsConfig {
			return DossierConfigService.getContactsConfig();
		},
		decimalSettings(): DecimalConfig {
			return DossierConfigService.getDecimalSettings();
		},
		autoFocusQty(): Boolean {
			return UserConfigService.getSaleSettings().NewSaleAutoFocusField == "qty";
		},
		autoFocusSku(): Boolean {
			return UserConfigService.getSaleSettings().NewSaleAutoFocusField == "sku";
		},
		showUnits(): Boolean {
			return this.UserConfigService.getSaleSettings().ShowUnits;
		},
		productSkuSm(): number {
			let total = 8;
			if (this.showUnits) {
				total -= 3;
			}
			if (this.showQty2) {
				total -= 3;
			}
			return total;
		},
		productSkuLg(): number {
			if (!this.showQty2 && !this.showUnits) {
				return 3;
			}
			let total = 4;
			if (this.showQty2) {
				total--;
			}
			if (this.showUnits) {
				total--;
			}
			return total;
		},
		descriptionLg(): number {
			let total = 7;
			if (this.showUnits || this.showQty2) {
				total -= 1;
			}
			if (this.showTrackingNUmber) {
				total -=  2;
			}
			if (this.showSizeSelect) {
				total -= 2;
			}
			return total;
		},
		unitLabel(): string | TranslateResult {
			let name = DossierConfigService.getGeneralConfig().UnitFieldName;
			if (name) {
				return name;
			}
			return this.$ct("sales-row.Unit");
		},
		discountsDisabled(): boolean {
			if (this.state.discount != 0) {
				return false;
			}
			if (this.row.Discount != 0) {
				return false;
			}
			if (!this.state.product) {
				return false;
			}
			return this.state.product.DiscountsDisabled;
		},
		hasLeeggoedModule(): boolean {
			return AuthService.hasModule(MODULE.DRANKENCENTRALE);
		},
		hasBackOrderModule(): boolean {
			return AuthService.hasModule(MODULE.BACKORDERBEHEER);
		},
		showDeliveredField():boolean {
			if (!this.saleEditState.config){
				return false;
			}
			return this.hasBackOrderModule && this.saleEditState.config.ShowAmountDeliveredField;
		},
		isDefaultCurrency(): boolean {
			return this.saleEditState.sale.CurrencyID == 1;
		},
		saleConfigs(): SaleConfigs {
			return this.DossierConfigService.getSaleConfigs();
		},
		showQty2(): boolean {
			return this.saleConfigs.UseQty2;
		},
		showTrackingNUmber():boolean {
			return this.saleEditState.config && this.saleEditState.config.UseProductTrackingNumbers && AuthService.hasModule(MODULE.WERKEN_MET_TRACERING) || false;
		},
		showSizeSelect():boolean {
			return AuthService.hasModule(MODULE.MATENTABEL) && !!this.state.product && !!this.state.product.SizeTableID;
		},
		sizeOptions():ISelectItem<number | null>[] {
			let items:ISelectItem<number | null>[] = [
				{text: this.$ct("no-size").toString(), value: null}
			];
			for (let entry of this.state.sizeEntries) {
				items.push({text: entry.Name, value: entry.ID});
			}
			return items;
		},
		qtyLabel(): TranslateResult {
			if (!this.showQty2) {
				return this.$ct("sales-row.Qty");
			}
			return this.$ct("sales-row.Qty1");
		},
		qty2Label(): TranslateResult | string {
			if (this.saleConfigs.Qty2Label) {
				return this.saleConfigs.Qty2Label;
			}
			return this.$ct("sales-row.Qty2");
		},
		unitValid(): boolean {
			if (!this.state.product) return true;
			return true;
		},
		addButtonDisabled(): boolean {
			if (this.fetchingProduct) return true;
			if (!this.unitValid) return true;
			return false;
		},
		showStockLocationId(): number {
			return DossierConfigService.getSaleConfigs().ToShowStockQtyInProductSearch;
		}
	},
	watch: {
		contactDiscount() {
			this.state.contactDiscount = this.contactDiscount || 0.0;
			this.state.discount = this.state.contactDiscount;
		},
		"sale.CurrencyID"() {
			this.state.currency = this.sale.CurrencyID;
			this.state.currencyRate = this.sale.CurrencyRate;
		},
		priceCategory() {
			if (this.state.product) {
				this.state.calculateRowUnitPrice();
			}
		},
		row: {
			immediate: true,
			// eslint-disable-next-line max-lines-per-function
			async handler(newRow: SaleRow) {
				// Ensure currency rate is set before calculating MC values
				this.state.currency = this.sale.CurrencyID;
				this.state.currencyRate = this.sale.CurrencyRate;

				this.state.productDescription = newRow.ProductDescription;
				this.state.amount = newRow.Amount;
				this.state.amount1 = newRow.Amount1;
				this.state.amount2 = newRow.Amount2;
				this.state.delivered = newRow.Delivered;
				this.state.unitPrice = newRow.UnitPrice;
				this.state.unitPriceMc = newRow.UnitPrice / this.state.currencyRate;
				this.state.discount = newRow.Discount;
				this.state.duration = newRow.Duration;
				this.state.unitBuyPrice = newRow.UnitBuyPrice;
				if (newRow.Product) {
					this.state.unitBuyPrice = newRow.Product.BuyNettoPrice || 0;
				}
				this.state.leeggoedTax = newRow.LeeggoedTax;
				this.state.ecoboniTax = newRow.EcoboniTax;
				this.state.accijnzenTax = newRow.AccijnzenTax;
				this.state.customVatExclusiveTax = newRow.CustomVatExclusiveTax;
				this.state.customVatInclusiveTax = newRow.CustomVatInclusiveTax;
				this.state.trackingNumber = newRow.TrackingNumber;
				this.state.sizeTableEntryID = newRow.SizeTableEntryID;
				//vat is managed by contact or product, this should not be changed on init
				//this.vat = parseFloat(newRow.Vat);
				this.state.setNetto(0, true);

				this.state.unit = newRow.UnitName;
				this.state.product = newRow.Product;

				if (this.vatRegime == 1) {
					this.state.vatItem = VatService.getVatByValue(newRow.Vat);
					if (this.contactsConfig.ContactSelectVatID) {
						if (this.saleEditState.sale.Contact && this.saleEditState.sale.Contact?.DefaultVatID != null) {
							this.state.vatItem = VatService.getVatById(this.saleEditState.sale.Contact?.DefaultVatID);
						}
					}
				} else if (this.vatRegime == 16) {
					this.state.vatItem = VatService.getVatByValue(6);
				} else if (this.vatRegime == 7) {
					this.state.vatItem = VatService.getVatByValue(newRow.Vat);
				}
				//if product not loaded, fetch product
				if (!this.state.product && newRow.ProductSku != "") {
					this.state.product = await ProductService.getProductBySku(newRow.ProductSku);
				}
				await this.state.loadSizeEntries();
			}
		},
		"state.amount"() {
			this.state.setNetto(0, true);
		},
		"state.vatMultiplier"() {
			this.state.updateBruto();
		},
		"state.discount"() {
			if (this.state.discount == null) {
				this.state.discount = 0;
			}
			if (Number.isNaN(this.state.discount)) {
				this.state.discount = 0;
			}
			this.state.setNetto(0, true);
		},
	},
	methods: {
		focusNext(ref: string) {
			let k = Object.keys(this.$ct("components.sales.sale-edit-fields"));
			let d = this.SaleSettings.SaleEditDisabledFields;

			let index = k.indexOf(ref);

			if (index === -1) {
				return;
			}

			for (let i = index + 1; i < k.length; i++) {
				if (!d.includes(k[i])) {
					if (this.$refs[k[i]]) {
						(this.$refs[k[i]] as any).focus();
						return;
					}
					this.focusNext(k[i]);
					return;
				}
			}
		},
		isDisabled(ref: string) {
			return this.SaleSettings.SaleEditDisabledFieldsDisable && this.SaleSettings.SaleEditDisabledFields.includes(ref);
		},
		async fetchProduct(sku: string) {
			let result = await ProductService.getProductBySku(sku);
			if (!result) return;
			this.state.onProductSelected(result);
		},
		onUnitPriceKeydown(event: KeyboardEvent) {
			this.onNumericKeyDown(event,"unitprice", this.decimalSettings.Unit);
			this.saleEditState.onUnitPriceKeyDown(event);
		},
		onDiscountKeydown(event: KeyboardEvent) {
			this.saleEditState.onDiscountKeyDown(event);
		},
		trigger(method: string) {
			(this as any)[method]();
		},
		hideTimePicker() {
			this.state.toggleTimePicker = false;
		},
		showTimePicker() {
			this.state.toggleTimePicker = true;
		},
		setNetto(value: number, preventUnitReset?: boolean) {
			if (this.isDefaultCurrency) {
				this.state.setNetto(value, preventUnitReset);
			} else {
				this.state.setNettoMc(value, preventUnitReset);
			}
		},
		setBruto(value: number, preventUnitReset?: boolean) {
			if (this.isDefaultCurrency) {
				this.state.setBruto(value, preventUnitReset);
			} else {
				this.state.setBrutoMc(value, preventUnitReset);
			}
		},
		setLeeggoed(value: number) {
			this.state.setLeeggoedPrice(value);
		},
		setUnitPrice(value: number) {
			if (this.isDefaultCurrency) {
				this.state.setUnitPrice(value);
			} else {
				this.state.setUnitMcPrice(value);
			}
		},
		setDuration(value: number) {
			this.state.setDuration(value);
		},
		async onProductSkuSelect(product: Product | null) {
			if (!product) {
				await this.saleEditState.onProductSelected(null);
				return;
			}
			// Skip re-fetching if the product hasn't changed
			if (this.saleEditState.productEditRowState.product != null && product.ID == this.saleEditState.productEditRowState.product.ID) {
				this.onSkuBlur();
				return;
			}
			this.state.productSkuChanged = true;
			await this.saleEditState.onProductSelected(product);
			//(this.$refs.productDescription as any).focus();
			this.onSkuBlur();
		},
		onSkuBlur() {
			this.focusNext("productSku");
			/*if(this.showUnits){
				(this.$refs.unit as any).focus();
			}else{
				(this.$refs.productDescription as any).focus();
			}*/
		},
		async onMultipleProductsSkuSelect(products: Product[]) {
			const rows = this.saleEditState.addMultipleProducts(products);
			for (const row of rows) {
				await SaleService.saleEditOnRowAddScript(this.saleEditState, row, services);
			}
			//await SaleService.saleEditOnRowAddScript(this.saleEditState, this.row);
		},
		// eslint-disable-next-line max-lines-per-function
		async add(skipNewRow: boolean = false) {
			//if (this.fetchingProduct) return;
			await new Promise((resolve) => setTimeout(resolve, 100)); // Wait for blurs to trigger
			let productSku = this.$refs.productSku as any;
			if (!productSku) return;
			await productSku.isReadyToBlur();
			(this.$refs.productSku as any).clear();
			this.state.setRowValues(this.row);

			this.state.toggleTimePicker = false;
			let scroll = false;
			let i = this.saleEditState.sale.Rows.indexOf(this.row);

			if(this.row.LedgerAccountCode==""){
				this.row.LedgerAccountCode = DossierConfigService.getBookkeepingConfig().DefaultSaleLedgerAccountCode;
			}

			if (i == -1) {
				scroll = true;
				this.row.SaleRowConfig.Hidden = this.saleEditState.addRowsAutoHidden;
				let rows = await this.saleEditState.ProductTrackingNumbersData.selectAutomaticTrackingNumbersForRow(this.row);
				for (let row of rows){
					this.saleEditState.sale.Rows.push(row);
					this.saleEditState.sale.Rows.push(...this.saleEditState.createTaxRows(row));
					this.saleEditState.sale.Rows.push(...this.saleEditState.createCompositionRows(row));
				}
			} else if (this.state.productSkuChanged) {
				this.saleEditState.sale.Rows.splice(i + 1, 0, ...this.saleEditState.createTaxRows(this.row), ...this.saleEditState.createCompositionRows(this.row));
			}

			this.saleEditState.updateTaxRows(this.saleEditState.sale.Rows.indexOf(this.row), this.row.Amount);

			if (UserConfigService.getSaleSettings().SelectNextLineOnProductSubmit) {
				if (this.saleEditState.selectNextRow()) {
					skipNewRow = true;
				}
			}

			await SaleService.saleEditOnRowAddScript(this.saleEditState, this.row, services);
			if (!skipNewRow) {
				this.saleEditState.selectedRow = new SaleRow();
				this.saleEditState.selectedRow.Discount = this.state.contactDiscount;
				this.state.discount = this.state.contactDiscount;
				this.state.leeggoedTax = 0;
				this.state.ledgerAccountCode = "";
			}

			this.saleEditState.processFavv();
			this.saleEditState.processFostplus();
			this.state.overrideMobileModalOpen = false;
			this.saleEditState.sale.calculateTotals();
			await this.saleEditState.runAutoSaleRowSettings();
			this.state.productSkuChanged = false;
			this.updateRecovery();
			setTimeout(() => {
				if (scroll && !skipNewRow) {
					this.$emit("scroll-to-bottom");
				}
				let el = this.$refs.qty as HTMLInputElement;
				let f = UserConfigService.getSaleSettings().NewSaleAutoFocusField;
				if (f == "sku") {
					el = this.$refs.productSku as HTMLInputElement;
				}
				if (skipNewRow && !UserConfigService.getSaleSettings().SelectNextLineOnProductSubmit) {
					el = this.$refs.unitPrice as HTMLInputElement;
				}
				if (!el) return;
				el.focus();
			}, 200);
		},
		onDescriptionChange() {
			//SkipNewRow is set to false, the product-row is update but no new row added.
			if (!UserConfigService.getSaleSettings().SkipSubmitOnDescriptionChange) {
				this.add(true);
			}
		},
		async updateRecovery() {
			let s = UserConfigService.getSaleSettings();
			if (s.LastSalesRecoveryID > 0) {
				this.saleEditState.recoverySale = await RecoveryService.put(s.LastSalesRecoveryID, this.sale);
			} else {
				this.saleEditState.recoverySale = await RecoveryService.post(this.sale);
				s.LastSalesRecoveryID = this.saleEditState.recoverySale.ID;
				await UserConfigService.setSaleSettings(s);
			}
		},
		insertTimeStamp() {
			console.log("Insert timestamp");
		},
		onKeyPress($event: KeyboardEvent) {
			//ctrl + 5 = timestamp
			if (($event.ctrlKey || $event.metaKey) && $event.keyCode == 53) {
				let target = $event.target as HTMLInputElement;
				let cp = target.selectionStart || 0;
				let space = cp != this.state.productDescription.length ? "" : " ";
				this.state.productDescription = this.state.productDescription.slice(0, cp)
					+ space
					+ printLongLocalDateTime(new Date())
					+ " " + this.state.productDescription.slice(cp);
			}
			this.onFocusableKeyUp($event);
		},
		onQty2Enter(event: KeyboardEvent) {
			event.preventDefault();
			requestAnimationFrame(() => {
				this.focusNext("qty2");
				//(this.$refs.productSku as any).focus();
			});
		},
		// eslint-disable-next-line max-lines-per-function
		async onTemplateKey(event: KeyboardEvent) {
			event.preventDefault();

			if (!this.sale.ContactID)
				return;

			this.saleEditState.processingMessage = this.$ct("components.sales.fetching-products");
			try {
				let isPurchase = this.saleEditState.isPurchaseType;
				let params = getEmptyIQueryParams();
				params.view.BrowseID = isPurchase ? BrowseID.PURCHASE_DOCUMENTS : BrowseID.SALES;
				params.view.Table = "Sale";
				params.limit = 1000;
				params.orderDirection = "DESC";

				let f = [] as HumanFilter[];
				let days = this.SaleSettings.TemplateSettingsDays;
				if (days) {
					f.push(new HumanFilter({
						Field: "this.InvoiceDate",
						Operator: "within days ago",
						Values: [`${days}`],
					}));
				}

				let types = this.SaleSettings.TemplateSettingsSaleTypes.map(t=>SaleService.getAllTypes().find(t2=>t2.ID == t)).filter(t=>{
					if (!t){
						return false;
					}
					if (isPurchase) {
						return t.ContactGroupID == 2;
					}
					return t.ContactGroupID != 2;
				}) as SaleType[];
				if (types.length == 0){
					let message = this.$ct("no-template-sale-types-configured");
					this.$wf.dialog(message, this.$ct("common.ok"), this.$ct("common.error"), "warning");
					return;
				}
				f.push(new HumanFilter({
					Field: "this.TypeID",
					Operator: "in",
					Values: types.map(v => `${v.ID}`)
				}));

				let sales = isPurchase
					? await SaleService.getPurchaseHistoryByContactID(this.sale.ContactID, params, "0", f)
					: await SaleService.getSaleHistoryByContactID(this.sale.ContactID, params, "0", f);
				let toAddRows: SaleRow[] = [];

				let skuCount = {} as any;
				sales.data.forEach((sale: Sale) => {
					sale.Rows.forEach((row: SaleRow) => {
						skuCount[row.ProductSku] = (skuCount[row.ProductSku] || 0) + 1;
					});
				});

				sales.data.forEach((sale: Sale) => {
					sale.Rows.forEach((row: SaleRow) => {
						if (row.ProductSku == "") return;
						if (skuCount[row.ProductSku] >= 3) {
							row.ID = 0;
							row.SaleID = this.sale.ID;
							row.Delivered = 0;
							row.Amount1 = 0;
							row.Amount2 = 1;
							row.Discount = this.state.contactDiscount;
							row.SaleRowConfig.Hidden = false;
							let i = toAddRows.findIndex(r => r.ProductSku == row.ProductSku);
							if (i != -1) return;
							toAddRows.push(row);
						}
					});
				});
				if (toAddRows.length == 0){
					let message = this.$ct("no-rows-found");
					this.$wf.dialog(message, this.$ct("common.ok"), this.$ct("common.info"), "info");
					return;
				}

				toAddRows = toAddRows.sort((a: SaleRow, b: SaleRow) => a.ProductDescription.localeCompare(b.ProductDescription));

				this.sale.Rows.push(...toAddRows);
				if (this.SaleSettings.TemplateSettingsUpdatePrices) {
					await this.saleEditState.updateAllRowsToLatestPrices();
				}
				this.saleEditState.sale.calculateTotals();
			} finally {
				this.saleEditState.processingMessage = null;
			}
		},
		onDeliveredEnter(event: KeyboardEvent) {
			event.preventDefault();
			requestAnimationFrame(() => {
				this.focusNext("delivered");
				/*if(this.showQty2){
					(this.$refs.qty2 as any).focus();
				}else{
					(this.$refs.productSku as any).focus();
				}*/
			});
		},
		onQtyEnter(event: KeyboardEvent) {
			event.preventDefault();
			requestAnimationFrame(() => {
				this.focusNext("qty");
				/*if(this.showQty2){
					(this.$refs.qty2 as any).focus();
				}else{
					(this.$refs.productSku as any).focus();
				}*/
			});
		},
		onUnitEnter(event: KeyboardEvent) {
			event.preventDefault();
			console.log("UNIT ENTER");
			requestAnimationFrame(() => {
				this.focusNext("unit");
				//(this.$refs.productDescription as any).focus();
			});
		},
		onTrackingNumberEnter(event: KeyboardEvent) {
			event.preventDefault();
			console.log("Tracking ENTER");
			requestAnimationFrame(() => {
				this.focusNext("trackingNumber");
				//(this.$refs.productDescription as any).focus();
			});
		},
		onProductSkuEnter() {
			if (!this.wait) {
				requestAnimationFrame(() => {
					this.onSkuBlur();
				});
			}
		},
		async onProductSkuTab(event:KeyboardEvent) {
			if (!this.wait) return;
			event.preventDefault();
			event.stopPropagation();
			if ((event as any).target.value == "") {
				return this.onSkuBlur();
			}
			// Skip fetch if product hasn't changed
			let inputSku = (event as any).target.value;
			if (this.state.product && this.state.product.Sku && this.state.product.Sku.toUpperCase() == inputSku.toUpperCase()) {
				this.onSkuBlur();
				return;
			}
			try{
				await this.fetchProduct(inputSku);
				if (!this.state.product) return;
				this.onSkuBlur();
			}catch(err){
			}
		},
		checkVatRegime() {
			if (this.vatRegime == 2) {
				this.$wf.notify(this.$ct("vat-warning").toString(), "danger", 3000, "exclamation-triangle");
			}
		},
		clearSku() {
			let ref = this.$refs.productSku as any;
			if (!ref) return;
			setTimeout(() => {
				ref.clear();
			}, 500);
		},
		focusSku() {
			let ref = this.$refs.productSku as any;
			if (!ref) return;
			ref.focus();
		},
		focusQty() {
			let ref = this.$refs.qty as any;
			if (!ref) return;
			ref.focus();
		},
		onFocusableKeyUpRowSelect(event: KeyboardEvent) {
			if (event.code == "ArrowUp") {
				event.preventDefault();
				event.stopPropagation();
				this.saleEditState.selectPreviousRow();
			} else if (event.code == "ArrowDown") {
				event.preventDefault();
				event.stopPropagation();
				this.saleEditState.selectNextRow();
			}
		},
		onFocusableKeyUp(event: KeyboardEvent) {
			let skuEl = this.$refs.productSku as any;
			if (skuEl) {
				if (skuEl.isOpened) {
					return;
				}
			}
			if (event.code == "ArrowUp") {
				event.preventDefault();
				event.stopPropagation();
				this.focusPreviousElement();
			} else if (event.code == "ArrowDown") {
				event.preventDefault();
				event.stopPropagation();
				this.focusNextElement();
			}
		},
		getFocusableElements(): NodeListOf<Element> {
			let query = `input:not([disabled]):not([tabindex="-1"]), .w-select-button:not([disabled]):not([tabindex="-1"])`;
			return this.$el.querySelectorAll(query);
		},
		focusNextElement() {
			let focusNext = false;
			for (let el of this.getFocusableElements()) {
				if (focusNext && (el as any).focus) {
					(el as any).focus();
					return;
				}
				if (el == document.activeElement) {
					focusNext = true;
				}
			}
		},
		focusPreviousElement() {
			let previousEl: Element | null = null;
			for (let el of this.getFocusableElements()) {
				if (el == document.activeElement) {
					if (!previousEl) return;
					if (!(previousEl as any).focus) return;
					(previousEl as any).focus();
					return;
				}
				previousEl = el;
			}
		},
		onPageDown(event: KeyboardEvent) {
			if (ScannerListener.couldFireSoon()) return;
			event.preventDefault();
			if (this.$wf.isModalOpen(this)) return;
			let el = this.$el as HTMLElement;
			if (!el) return;
			if (!document.activeElement) return;
			if (!isChildOf(el, document.activeElement as HTMLElement)) return;
			this.add();
		},
		onKeyUp(event: KeyboardEvent) {
			if (event.key == "PageDown") {
				event.preventDefault();
				this.onPageDown(event);
			}
			if (event.key == "Enter") {
				event.preventDefault();
				if ((event.target as HTMLTextAreaElement).className.indexOf("w-input") >= 0 ||
					(event.target as HTMLTextAreaElement).className.indexOf("w-textarea") >= 0 ||
					(event.target as HTMLTextAreaElement).className.indexOf("ql-editor") >= 0 ||
					(event.target as HTMLTextAreaElement).className.indexOf("w-select-button") >= 0 ||
					(event.target as HTMLTextAreaElement).className.indexOf("editor-area") >= 0) {
					return;
				}
				(this.$refs.qty as any).focus();
			}
		},
		onKeyDown(event: KeyboardEvent) {
			if (event.key == "PageDown") {
				event.stopImmediatePropagation();
				event.preventDefault();
			}
		},
		onNumericKeyDown(event: KeyboardEvent, ref: string, decimals: number) {
			if (event.key == "F5") {
				event.stopImmediatePropagation();
				event.preventDefault();
				this.toggleCalculator = true;
				this.calculatorRef = ref;
				this.calculatorDecimals = decimals || 2;
			}
		},
		onUnitPriceBlur() {
			this.focusNext("unitPrice");
			/*if (this.discountsDisabled){
				if (this.vatRegime == 2){
					if (this.$refs.netto) (this.$refs.netto as any).focus();
				}else if (this.$refs.vatItems) (this.$refs.vatItems as any).focus();
			}else if (this.$refs.discount) {
				(this.$refs.discount as any).focus();
			}*/
		},
		onProductDescriptionBlur() {
			this.focusNext("productDescription");
			//if (!this.$refs.unitPrice) return;
			//(this.$refs.unitPrice as any).focus();
		},
		onDiscountBlur() {
			if (this.vatRegime == 2) {
				this.focusNext("vatItems");
			} else {
				this.focusNext("discount");
			}
			/*if (this.vatRegime == 2) {
				if (!this.$refs.netto) return;
				(this.$refs.netto as any).focus();
			}else{
				if (!this.$refs.vatItems) return;
				(this.$refs.vatItems as any).focus();
			}*/
		},
		onVatItemsBlur() {
			this.focusNext("vatItems");
			/*if (!this.$refs.netto) return;
			(this.$refs.netto as any).focus();*/
		},
		onNettoBlur() {
			this.focusNext("netto");
			/*if (this.hasLeeggoedModule){
				if (!this.$refs.leeggoed) return;
				(this.$refs.leeggoed as any).focus();
			}else{
				if (!this.$refs.bruto) return;
				(this.$refs.bruto as any).focus();
			}*/
		},
		onLeeggoedBlur() {
			this.focusNext("leeggoed");
			/*if (!this.$refs.bruto) return;
			(this.$refs.bruto as any).focus();*/
		},
		onBrutoFocus() {
			this.brutoOnFocus = this.isDefaultCurrency ? this.state.bruto : this.state.brutoMc;
		},
		onBrutoBlur() {
			let currentBruto = this.isDefaultCurrency ? this.state.bruto : this.state.brutoMc;
			let brutoChanged = currentBruto !== this.brutoOnFocus;

			if (this.SaleSettings.GrossTotalIsPerUnit && brutoChanged) {
				// Convert bruto total to bruto per unit, then set unit price
				if (this.isDefaultCurrency) {
					this.state.setBrutoPerUnit(this.state.bruto);
				} else {
					this.state.setBrutoPerUnitMc(this.state.brutoMc);
				}
			} else if (!this.SaleSettings.GrossTotalIsPerUnit) {
				this.state.updateBruto();
			}
			if (!this.$refs.buttonAdd) return;
			(this.$refs.buttonAdd as any).focus();
		},
		onBrutoKeyUpEnter(){
			if (!this.$refs.buttonAdd) return;
			(this.$refs.buttonAdd as any).focus();
		},
		updateCalculatorRef(val: number) {
			switch (this.calculatorRef) {
			case "qty":
				this.state.setAmount1(val);
				break;
			case "qty2":
				this.state.setAmount2(val);
				break;
			case "netto":
				this.state.setNetto(val);
				break;
			case "leeggoed":
				this.state.setLeeggoedPrice(val);
				break;
			case "bruto":
				this.state.setBruto(val);
				break;
			case "unitprice":
				this.state.setUnitPrice(val);
				break;
			}
		},
		openProductSearchBrowse(data:IOpenProductSearchBrowseData) {
			if (!this.$refs.productSku) return;
			(this.$refs.productSku as any).openSearchBrowse(data.searchString);
		},
		openProductCreateView(sku:string) {
			if (!this.$refs.productSku) return;
			(this.$refs.productSku as any).openCreateProduct(sku);
		}
	},
	created() {
		this.state.on("clearSku", this.clearSku);
		this.state.on("focusSku", this.focusSku);
		this.state.on("focusQty", this.focusQty);
		this.state.on("open-product-search-browse", this.openProductSearchBrowse);
		this.state.on("open-create-product-view", this.openProductCreateView);
		window.addEventListener("keyup", this.onKeyUp);
		window.addEventListener("keydown", this.onKeyDown, true);
	},
	destroyed() {
		this.state.off("clearSku", this.clearSku);
		this.state.off("focusSku", this.focusSku);
		this.state.off("focusQty", this.focusQty);
		this.state.off("open-product-search-browse", this.openProductSearchBrowse);
		this.state.off("open-create-product-view", this.openProductCreateView);
		window.removeEventListener("keyup", this.onKeyUp);
		window.removeEventListener("keydown", this.onKeyDown, true);
	},
	mounted() {
		this.$emit("state", this.state);
		this.state.currency = this.sale.CurrencyID;
		this.state.currencyRate = this.sale.CurrencyRate;
		this.state.contactDiscount = this.contactDiscount || 0.0;
		this.state.discount = this.state.contactDiscount;
	},
	components: {
		AppProductSkuAutocomplete,
		AppVatSelect,
		AppUnitAutocomplete,
		AppInputWithTextareaModal,
		AppDuration,
		AppSaleEditRowDiscount,
		AppCalculator,
		AppSaleEditRowTrackingNumberSelect
	},
	i18nEx: {
		componentPrefix: "components.products-table"
	}
});
